// src/components/UserTable.js

import React, { useState, useMemo } from 'react';
import { 
  Table, 
  TableHead, 
  TableBody, 
  TableRow, 
  TableCell, 
  Paper, 
  TableSortLabel,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Stack,
  TextField,
} from '@mui/material';
import UserRow from './UserRow';
import { Refresh as RefreshIcon, Search as SearchIcon } from '@mui/icons-material';

function UserTable({ users, onToggleAdmin, onToggleCanBook, onDeactivate, onToggleElectricCar, currentUserId }) {
  const [order, setOrder] = useState('asc');
  const [orderBy, setOrderBy] = useState('email');

  const [adminFilter, setAdminFilter] = useState('all'); 
  const [canBookFilter, setCanBookFilter] = useState('all'); 
  const [electricCarFilter, setElectricCarFilter] = useState('all'); 
  const [searchQuery, setSearchQuery] = useState(''); 

  const handleSortRequest = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const resetFilters = () => {
    setAdminFilter('all');
    setElectricCarFilter('all');
    setCanBookFilter('all');
    setSearchQuery('');
  };

  const sortedAndFilteredUsers = useMemo(() => {
    let filteredUsers = [...users];

    if (adminFilter === 'admins') {
      filteredUsers = filteredUsers.filter(user => user.is_admin);
    } else if (adminFilter === 'non-admins') {
      filteredUsers = filteredUsers.filter(user => !user.is_admin);
    }

    if (canBookFilter === 'can-book') {
      filteredUsers = filteredUsers.filter(user => user.can_book);
    } else if (canBookFilter === 'can-not-book') {
      filteredUsers = filteredUsers.filter(user => !user.can_book);
    }

    if (electricCarFilter === 'with') {
      filteredUsers = filteredUsers.filter(user => user.has_electric_car);
    } else if (electricCarFilter === 'without') {
      filteredUsers = filteredUsers.filter(user => !user.has_electric_car);
    }

    if (searchQuery.trim() !== '') {
      filteredUsers = filteredUsers.filter(user => 
        user.email.toLowerCase().includes(searchQuery.trim().toLowerCase())
      );
    }

    filteredUsers.sort((a, b) => {
      let aValue, bValue;

      if (orderBy === 'email') {
        aValue = a.email.toLowerCase();
        bValue = b.email.toLowerCase();
      } else if (orderBy === 'created_date') {
        aValue = new Date(a.created_date);
        bValue = new Date(b.created_date);
      } else {
        aValue = a[orderBy];
        bValue = b[orderBy];
      }

      if (aValue < bValue) {
        return order === 'asc' ? -1 : 1;
      }
      if (aValue > bValue) {
        return order === 'asc' ? 1 : -1;
      }
      return 0;
    });

    return filteredUsers;
  }, [users, order, orderBy, adminFilter, canBookFilter, electricCarFilter, searchQuery]);

  return (
    <Paper sx={{ p: 2 }}>
      <Stack spacing={2} direction={{ xs: 'column', md: 'row' }} alignItems="center" sx={{ mb: 3 }}>
        <Stack sx={{ width: { xs: '100%', md: '33%' } }}>
          <TextField
            fullWidth
            variant="outlined"
            size="small"
            placeholder="Search by Email"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: <SearchIcon position="start" />,
            }}
          />
        </Stack>

        <Stack sx={{ width: { xs: '100%', md: '25%' } }}>
          <FormControl fullWidth variant="outlined" size="small">
            <InputLabel id="admin-filter-label">Admin Status</InputLabel>
            <Select
              labelId="admin-filter-label"
              id="admin-filter"
              value={adminFilter}
              onChange={(e) => setAdminFilter(e.target.value)}
              label="Admin Status"
            >
              <MenuItem value="all">All</MenuItem>
              <MenuItem value="admins">Admins</MenuItem>
              <MenuItem value="non-admins">Non-Admins</MenuItem>
            </Select>
          </FormControl>
        </Stack>

        <Stack sx={{ width: { xs: '100%', md: '25%' } }}>
          <FormControl fullWidth variant="outlined" size="small">
            <InputLabel id="can-book-filter-label">Can Book</InputLabel>
            <Select
              labelId="admin-filter-label"
              id="admin-filter"
              value={adminFilter}
              onChange={(e) => setCanBookFilter(e.target.value)}
              label="Admin Status"
            >
              <MenuItem value="all">All</MenuItem>
              <MenuItem value="admins">Can Book</MenuItem>
              <MenuItem value="non-admins">Can not book</MenuItem>
            </Select>
          </FormControl>
        </Stack>

        <Stack sx={{ width: { xs: '100%', md: '25%' } }}>
          <FormControl fullWidth variant="outlined" size="small">
            <InputLabel id="electric-car-filter-label">Electric Car</InputLabel>
            <Select
              labelId="electric-car-filter-label"
              id="electric-car-filter"
              value={electricCarFilter}
              onChange={(e) => setElectricCarFilter(e.target.value)}
              label="Electric Car"
            >
              <MenuItem value="all">All</MenuItem>
              <MenuItem value="with">With Electric Car</MenuItem>
              <MenuItem value="without">Without Electric Car</MenuItem>
            </Select>
          </FormControl>
        </Stack>

        <Stack sx={{ width: { xs: '100%', md: '17%' } }}>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={resetFilters}
            fullWidth
          >
            Reset
          </Button>
        </Stack>
      </Stack>

      <Table>
        <TableHead>
          <TableRow>
            <TableCell>
              <TableSortLabel
                active={orderBy === 'email'}
                direction={orderBy === 'email' ? order : 'asc'}
                onClick={() => handleSortRequest('email')}
              >
                Email
              </TableSortLabel>
            </TableCell>

            <TableCell align="center">
              <TableSortLabel
                active={orderBy === 'is_admin'}
                direction={orderBy === 'is_admin' ? order : 'asc'}
                onClick={() => handleSortRequest('is_admin')}
              >
                Admin
              </TableSortLabel>
            </TableCell>

            <TableCell align="center">
              <TableSortLabel
                active={orderBy === 'can_book'}
                direction={orderBy === 'can_book' ? order : 'asc'}
                onClick={() => handleSortRequest('can_book')}
              >
                Can Book
              </TableSortLabel>
            </TableCell>

            <TableCell align="center">
              <TableSortLabel
                active={orderBy === 'has_electric_car'}
                direction={orderBy === 'has_electric_car' ? order : 'asc'}
                onClick={() => handleSortRequest('has_electric_car')}
              >
                Electric Car
              </TableSortLabel>
            </TableCell>

            <TableCell align="center">Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {sortedAndFilteredUsers.map(user => (
            <UserRow 
              key={user.id} 
              user={user} 
              onToggleAdmin={onToggleAdmin} 
              onToggleCanBook={onToggleCanBook} 
              onDeactivate={onDeactivate}
              onToggleElectricCar={onToggleElectricCar}
              currentUserId={currentUserId} 
            />
          ))}
          {sortedAndFilteredUsers.length === 0 && (
            <TableRow>
              <TableCell colSpan={5} align="center">No users found.</TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </Paper>
  );
}

export default UserTable;
